import type { EnrichedReadyToSharePlatformPostV2 } from '#src/app/shared/api/readyToShare';

import type { FacebookGroupPostComponentsProps } from '../components/facebookGroupPost/types';
import { useFacebookGroupPostComponents } from '../components/facebookGroupPost/useFacebookGroupPostComponents';
import type { FacebookPostComponentsProps } from '../components/facebookPost/types';
import { useFacebookPostComponents } from '../components/facebookPost/useFacebookPostComponents';
import type { FacebookStoryComponentsProps } from '../components/facebookStory/types';
import { useFacebookStoryComponents } from '../components/facebookStory/useFacebookStoryComponents';
import type { InstagramPostComponentsProps } from '../components/instagramPost/types';
import { useInstagramPostComponents } from '../components/instagramPost/useInstagramPostComponents';
import type { InstagramStoryComponentsProps } from '../components/instagramStory/types';
import { useInstagramStoryComponents } from '../components/instagramStory/useInstagramStoryComponents';
import type { RedditPostComponentsProps } from '../components/redditGroupPost/types';
import { useRedditPostComponents } from '../components/redditGroupPost/useRedditGroupPostComponents';
import type { PostPreviewProps, SubComponentsReturnType } from '../types';

type ComponentHook<P> = (props: P) => SubComponentsReturnType;
type Executor = (props: PostPreviewProps) => SubComponentsReturnType;

const createEntry = <P>(hook: ComponentHook<P>, getProps: (props: PostPreviewProps) => P): Executor => {
	return (props) => {
		const prepared = getProps(props);
		return hook(prepared);
	};
};

// Add more executors as more post types are supported
export const EXECUTORS: Readonly<Record<EnrichedReadyToSharePlatformPostV2['__typename'], Executor>> = {
	ReadyToShareFacebookPost: createEntry(useFacebookPostComponents, (props): FacebookPostComponentsProps => {
		if ('message' in props.sharingResource) {
			const {
				loading,
				size,
				retryState,
				sharingResource: { message, previewImageUrl },
			} = props;

			return { loading, message, previewImageUrl, size, retryState };
		}
		throw new Error('Unexpected sharing resource for post type', {
			cause: { postType: 'ReadyToShareFacebookPost', sharingResource: props.sharingResource },
		});
	}),
	ReadyToShareFacebookGroupsPost: createEntry(
		useFacebookGroupPostComponents,
		(props): FacebookGroupPostComponentsProps => {
			if ('message' in props.sharingResource && 'recommendedGroup' in props.sharingResource) {
				const {
					loading,
					size,
					retryState,
					sharingResource: { message, previewImageUrl, recommendedGroup },
				} = props;

				return { loading, message, previewImageUrl, recommendedGroup, size, retryState };
			}
			throw new Error('Unexpected sharing resource for post type', {
				cause: { postType: 'ReadyToShareFacebookGroupsPost', sharingResource: props.sharingResource },
			});
		},
	),
	ReadyToShareFacebookStory: createEntry(useFacebookStoryComponents, (props): FacebookStoryComponentsProps => {
		const {
			loading,
			size,
			retryState,
			sharingResource: { previewImageUrl },
		} = props;

		return { loading, previewImageUrl, size, retryState };
	}),
	ReadyToShareInstagramPost: createEntry(useInstagramPostComponents, (props): InstagramPostComponentsProps => {
		if ('message' in props.sharingResource) {
			const {
				loading,
				size,
				retryState,
				sharingResource: { message, previewImageUrl },
			} = props;

			return { loading, message, previewImageUrl, size, retryState };
		}
		throw new Error('Unexpected sharing resource for post type', {
			cause: { postType: 'ReadyToShareInstagramPost', sharingResource: props.sharingResource },
		});
	}),
	ReadyToShareInstagramStory: createEntry(useInstagramStoryComponents, (props): InstagramStoryComponentsProps => {
		const {
			loading,
			size,
			retryState,
			sharingResource: { previewImageUrl },
		} = props;

		return { loading, previewImageUrl, size, retryState };
	}),
	ReadyToShareRedditGroupsPost: createEntry(useRedditPostComponents, (props): RedditPostComponentsProps => {
		if ('message' in props.sharingResource && 'recommendedGroup' in props.sharingResource && props.petition) {
			const {
				loading,
				size,
				retryState,
				sharingResource: { message, previewImageUrl, recommendedGroup },
				petition,
			} = props;

			return { loading, message, previewImageUrl, subreddit: recommendedGroup, size, petition, retryState };
		}
		throw new Error('Unexpected sharing resource for post type', {
			cause: { postType: 'ReadyToShareRedditGroupsPost', sharingResource: props.sharingResource },
		});
	}),
};
